// Вид замков на карте мира. У каждой расы свой набор картинок, у людей — заводской; поверх него игрок может
// поставить купленный скин: на один замок или сразу на все, на срок или навсегда.
import type { RaceKey } from './types.js';

/** Уровни, с которых у наборов меняется картинка. */
export const CASTLE_SKIN_LEVELS = [1, 5, 10, 15, 20, 25] as const;

/** Наборы рас: у людей заводские замки. */
export const RACE_CASTLE_SETS: Partial<Record<RaceKey, string>> = {
  elves: 'forest_elves',
  dwarves: 'copper_dwarves',
  orcs: 'bloodfang_orcs',
};

/** Скины на продажу. */
export const CASTLE_SKINS = ['deep_lord', 'undead', 'crimson_empire', 'moon_crystal', 'blue_rock'] as const;
export type CastleSkinKey = (typeof CASTLE_SKINS)[number];

/** Сроки аренды скина в днях. */
export const CASTLE_SKIN_DAYS = [30, 60, 90, 180] as const;

export interface CastleSkinPrices {
  /** Цены в золоте: дни → цена, forever — навсегда. */
  castle: Record<string, number>;
  all: Record<string, number>;
}

/** Цена скина: castleId null — на все замки; days null — навсегда. */
export function castleSkinPrice(
  prices: CastleSkinPrices,
  scope: 'castle' | 'all',
  days: number | null,
): number | null {
  return prices[scope][days == null ? 'forever' : String(days)] ?? null;
}

export interface ActiveSkinRow {
  skin: string;
  scope: string;
  castleId: string | null;
  expiresAt: Date | null;
  appliedAt: Date;
}

/** Скин, который сейчас на замке: свой у замка, иначе «на все замки»; из нескольких — поставленный последним. */
export function activeCastleSkin(rows: ActiveSkinRow[], castleId: string, now = new Date()): string | null {
  const alive = rows
    .filter((r) => r.expiresAt == null || r.expiresAt > now)
    .sort((a, b) => b.appliedAt.getTime() - a.appliedAt.getTime());
  return (
    alive.find((r) => r.scope === 'castle' && r.castleId === castleId)?.skin ??
    alive.find((r) => r.scope === 'all')?.skin ??
    null
  );
}

/** Какой набор картинок показать: купленный скин, иначе набор расы; null — заводской замок. */
export function castleArtSet(
  race: string | null | undefined,
  skin: string | null | undefined,
): string | null {
  return skin || (race ? (RACE_CASTLE_SETS[race as RaceKey] ?? null) : null);
}

/** Уровень картинки набора: последняя стадия не выше уровня замка. */
export function castleSkinStage(level: number): number {
  return [...CASTLE_SKIN_LEVELS].reverse().find((stage) => level >= stage) ?? 1;
}
